import { webStorage } from "@/lib/web-storage"

export type TravelAssistantEntrySource =
  | "chat"
  | "signup"
  | "site_nav"
  | "you"
  | "home"

export type PendingAuthAction = {
  type: "setup-travel-assistant"
  source?: TravelAssistantEntrySource
  returnToChatId?: number
  requiresAuthentication?: boolean
  /** Open the register gate when the product home loads. */
  showRegisterGateOnHome?: boolean
}

const STORAGE_KEY = "mindtrip.pending_auth_action" as const

export function setPendingAuthAction(action: PendingAuthAction) {
  webStorage.setJSON(STORAGE_KEY, action, { type: "session" })
}

export function getPendingAuthAction(): PendingAuthAction | null {
  return webStorage.tryGetJSON<PendingAuthAction>(STORAGE_KEY, {
    type: "session",
  })
}

export function shouldDeferPostSignupPrompts() {
  return getPendingAuthAction()?.type === "setup-travel-assistant"
}

export function shouldSkipCompletedPostAuthSetup(
  action: PendingAuthAction,
  hasSetUpTravelAssistant: boolean | undefined
) {
  if (!hasSetUpTravelAssistant) return false

  // `source: "signup"` supports handoffs written before
  // `requiresAuthentication` was added.
  return !!action.requiresAuthentication || action.source === "signup"
}

export function skipClaimHandlePromptForPendingSetup() {
  const shouldSkip = shouldDeferPostSignupPrompts()
  if (shouldSkip) {
    webStorage.setItem("seenClaimHandleDialog", "true")
  }
  return shouldSkip
}

export async function runPendingTravelAssistantSetup(
  action: PendingAuthAction,
  start: (
    source: TravelAssistantEntrySource,
    returnToChatId?: number
  ) => Promise<boolean>
) {
  skipClaimHandlePromptForPendingSetup()

  const started = await start(action.source ?? "chat", action.returnToChatId)
  if (started) {
    clearPendingAuthAction()
  }

  return started
}

export function prepareTravelAssistantSetupAfterSignup(callbackUrl?: string) {
  if (getPendingAuthAction()) return callbackUrl

  const chatIdMatch = callbackUrl?.match(/^\/chat\/(\d+)(?:[/?#]|$)/)
  const returnToChatId = chatIdMatch ? Number(chatIdMatch[1]) : undefined

  setPendingAuthAction({
    type: "setup-travel-assistant",
    source: "signup",
    returnToChatId,
    requiresAuthentication: true,
  })
  return returnToChatId ? callbackUrl : "/chat/new"
}

export function clearPendingAuthAction() {
  webStorage.removeItem(STORAGE_KEY, { type: "session" })
}
